import { z } from 'zod'

// Data source schema
export const DataSourceSchema = z.object({
  name: z.enum(['website', 'companiesHouse']),
  status: z.enum(['success', 'failed', 'skipped']),
  fetchedAt: z.string().nullable(),
  reference: z.string().nullable(),
})

export type DataSource = z.infer<typeof DataSourceSchema>

// Firmographics schema
export const FirmographicsSchema = z.object({
  legalName: z.string().nullable(),
  companyNumber: z.string().nullable(),
  status: z.string().nullable(),
  incorporatedDate: z.string().nullable(),
  companyType: z.string().nullable(),
  sicCodes: z.array(z.string()),
  sector: z.string().nullable(),
  registeredAddress: z.string().nullable(),
  website: z.string().nullable(),
})

export type Firmographics = z.infer<typeof FirmographicsSchema>

// Size schema
export const SizeSchema = z.object({
  turnoverBand: z.string().nullable(),
  employeeEstimate: z.string().nullable(),
  accountsCategory: z.string().nullable(),
  latestFilingDate: z.string().nullable(),
  growthSignals: z.array(z.string()),
})

export type Size = z.infer<typeof SizeSchema>

// Director schema
export const DirectorSchema = z.object({
  name: z.string(),
  role: z.string(),
  appointedDate: z.string().nullable(),
})

export type Director = z.infer<typeof DirectorSchema>

// Digital presence schema
export const DigitalSchema = z.object({
  platform: z.string().nullable(),
  techDetected: z.array(z.string()),
  mobileExperience: z.string().nullable(),
})

export type Digital = z.infer<typeof DigitalSchema>

// Fit Assessment schema (customised to user's business)
export const FitAssessmentSchema = z.object({
  fitScore: z.number().min(1).max(5),
  fitRationale: z.string().nullable(),
  primaryNeeds: z.array(z.string()),
  opportunities: z.array(z.string()),
})

export type FitAssessment = z.infer<typeof FitAssessmentSchema>

// Outreach angle schema
export const OutreachAngleSchema = z.object({
  hook: z.string(),
  message: z.string(),
})

export type OutreachAngle = z.infer<typeof OutreachAngleSchema>

// AI output schema (what AI generates)
export const AIOutputSchema = z.object({
  overview: z.string(),
  sector: z.string().nullable(),
  growthSignals: z.array(z.string()),
  digital: DigitalSchema,
  fitAssessment: FitAssessmentSchema,
  outreachAngles: z.array(OutreachAngleSchema),
})

export type AIOutput = z.infer<typeof AIOutputSchema>

// Full company intelligence schema
export const CompanyIntelligenceSchema = z.object({
  generatedAt: z.string(),
  confidence: z.enum(['low', 'medium', 'medium-high', 'high']),
  sources: z.array(DataSourceSchema),
  overview: z.string(),
  firmographics: FirmographicsSchema,
  size: SizeSchema,
  directors: z.array(DirectorSchema),
  digital: DigitalSchema,
  fitAssessment: FitAssessmentSchema,
  outreachAngles: z.array(OutreachAngleSchema),
})

export type CompanyIntelligence = z.infer<typeof CompanyIntelligenceSchema>

// Companies House match candidate
export interface CompaniesHouseMatch {
  company_number: string
  title: string
  company_status?: string
  date_of_creation?: string
  similarity?: number
}

// Research state for UI
export interface ResearchState {
  status: 'idle' | 'fetching-website' | 'fetching-companies-house' | 'generating-summary' | 'complete' | 'error' | 'awaiting-company-selection'
  progress: {
    website: 'pending' | 'success' | 'failed' | 'skipped'
    companiesHouse: 'pending' | 'success' | 'failed' | 'skipped' | 'awaiting-selection'
    ai: 'pending' | 'success' | 'failed' | 'skipped'
  }
  companiesHouseMatches?: CompaniesHouseMatch[]
  error?: string
}

// Raw data types from API
export interface WebsiteData {
  url: string
  fetchedAt: string
  title: string | null
  metaDescription: string | null
  ogTitle: string | null
  ogDescription: string | null
  ogImage: string | null
  schemaOrg: object[]
  bodyText: string
}

export interface CompaniesHouseCompany {
  company_name: string
  company_number: string
  company_status: string
  date_of_creation: string
  type: string
  sic_codes?: string[]
  registered_office_address?: {
    address_line_1?: string
    address_line_2?: string
    locality?: string
    postal_code?: string
    country?: string
  }
  accounts?: {
    accounting_reference_date?: {
      day: string
      month: string
    }
  }
  last_accounts?: {
    made_up_to?: string
  }
}

export interface CompaniesHouseOfficer {
  name: string
  officer_role: string
  appointed_on?: string
  resigned_on?: string
}

export interface CompaniesHouseData {
  company?: CompaniesHouseCompany
  officers?: CompaniesHouseOfficer[]
  matches?: CompaniesHouseMatch[]
  fetchedAt: string
  notConfigured?: boolean
  autoSelected?: boolean
}

export interface ResearchRawData {
  fetchedAt: string
  website: WebsiteData | null
  companiesHouse: CompaniesHouseData | null
}

// Research settings
export interface ResearchSettings {
  autoResearchNewCompanies: boolean
  includeCompaniesHouse: boolean
  businessContext: string
}

export const DEFAULT_RESEARCH_SETTINGS: ResearchSettings = {
  autoResearchNewCompanies: true,
  includeCompaniesHouse: true,
  businessContext: '',
}
